<template>
    <section class="course-learn">
        <div class="container course-learn__container" :class="{ 'is-embedded': embedded }">
            <StudentSidebar v-if="!embedded" :student="student" />

            <main class="course-learn__main">
                <header class="course-learn__hero">
                    <div class="course-learn__hero-copy">
                        <span class="course-learn__eyebrow">Course workspace</span>
                        <h1>{{ course.title || 'My Course' }}</h1>
                    </div>

                    <a :href="backUrl" class="course-learn__back">
                        <i class="ri-arrow-left-line"></i>
                        {{ backLabel }}
                    </a>
                </header>

                <section v-if="course.description" class="course-learn__overview">
                    <div class="course-learn__overview-head">
                        <div>
                            <span class="course-learn__eyebrow">Course overview</span>
                            <h2>About This Course</h2>
                        </div>

                        <button v-if="shouldShowCourseDescriptionToggle" type="button"
                            @click="courseDescriptionExpanded = !courseDescriptionExpanded">
                            {{ courseDescriptionExpanded ? 'See Less' : 'See More' }}
                            <i :class="courseDescriptionExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                        </button>
                    </div>

                    <p class="course-learn__rich-description"
                        :class="{ 'is-collapsed': shouldShowCourseDescriptionToggle && !courseDescriptionExpanded }">
                        {{ course.description }}
                    </p>
                </section>

                <section class="course-learn__stats" aria-label="Course summary">
                    <article v-for="stat in summaryStats" :key="stat.label" class="course-learn__stat">
                        <i :class="stat.icon"></i>
                        <span>{{ stat.label }}</span>
                        <strong>{{ stat.value }}</strong>
                    </article>
                </section>

                <section class="course-learn__workspace">
                    <aside class="course-learn__curriculum" aria-label="Course curriculum">
                        <div class="course-learn__curriculum-head">
                            <div>
                                <span class="course-learn__eyebrow">Curriculum</span>
                                <h2>Chapters & Lessons</h2>
                            </div>

                            <span>{{ completedLessonIds.length }}/{{ lessons.length }}</span>
                        </div>

                        <div v-if="course.chapters?.length" class="course-learn__chapter-list">
                            <article v-for="(chapter, chapterIndex) in course.chapters" :key="chapter.id"
                                class="course-learn__chapter" :class="{
                                    'is-open': isChapterOpen(chapter.id),
                                    'is-locked': chapter.locked,
                                    'is-completed': isChapterCompleted(chapter),
                                }"
                                :data-lock-message="chapter.locked ? chapterLockMessage(chapter) : null"
                                :title="chapter.locked ? chapterLockMessage(chapter) : null">
                                <button type="button" class="course-learn__chapter-toggle"
                                    :disabled="chapter.locked"
                                    :title="chapter.locked ? chapterLockMessage(chapter) : null"
                                    @click="toggleChapter(chapter.id)">
                                    <span>{{ chapterIndex + 1 }}</span>

                                    <strong>{{ chapter.title }}</strong>

                                    <small>{{ chapter.locked ? 'Locked' : `${chapter.lessons?.length || 0} lesson${(chapter.lessons?.length || 0) === 1 ? '' : 's'}` }}</small>

                                    <i
                                        :class="chapter.locked ? 'ri-lock-line' : (isChapterOpen(chapter.id) ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line')"></i>
                                </button>

                                <div v-show="isChapterOpen(chapter.id)" class="course-learn__chapter-body">
                                    <button v-for="lesson in chapter.lessons" :key="lesson.id" type="button"
                                        class="course-learn__lesson" :class="{
                                            'is-active': activeLesson?.id === lesson.id && activePanel === 'lesson',
                                            'is-completed': isLessonCompleted(lesson.id),
                                            'is-locked': !isLessonUnlocked(lesson, chapter.id),
                                        }" :disabled="!isLessonUnlocked(lesson, chapter.id)"
                                        :data-lock-message="!isLessonUnlocked(lesson, chapter.id) ? lessonLockMessage(lesson, chapter) : null"
                                        :title="!isLessonUnlocked(lesson, chapter.id) ? lessonLockMessage(lesson, chapter) : null"
                                        @click="selectLesson(lesson, chapter.id)">
                                        <i :class="lessonIcon(lesson)"></i>

                                        <span>
                                            <strong>{{ lesson.title }}</strong>
                                            <small>{{ lessonTypeLabel(lesson) }} · {{ lesson.durationLabel }}</small>
                                        </span>

                                        <i v-if="isLessonCompleted(lesson.id)" class="ri-checkbox-circle-fill"></i>
                                    </button>

                                    <div v-if="chapterHasResources(chapter)"
                                        class="course-learn__chapter-resources">
                                        <button v-if="chapterExamCount(chapter)" type="button"
                                            :disabled="chapter.locked || !isChapterResourcesUnlocked(chapter)"
                                            :data-lock-message="!isChapterResourcesUnlocked(chapter) ? chapterResourceLockMessage(chapter) : null"
                                            :title="!isChapterResourcesUnlocked(chapter) ? chapterResourceLockMessage(chapter) : null"
                                            @click="activePanel = 'exams'">
                                            <i class="ri-file-paper-2-line"></i>
                                            {{ chapterExamCount(chapter) }} Exam{{ chapterExamCount(chapter) === 1 ? ''
                                            : 's' }}
                                        </button>

                                        <button v-if="chapterQuizCount(chapter)" type="button"
                                            :disabled="chapter.locked || !isChapterResourcesUnlocked(chapter)"
                                            :data-lock-message="!isChapterResourcesUnlocked(chapter) ? chapterResourceLockMessage(chapter) : null"
                                            :title="!isChapterResourcesUnlocked(chapter) ? chapterResourceLockMessage(chapter) : null"
                                            @click="activePanel = 'quizzes'">
                                            <i class="ri-question-line"></i>
                                            {{ chapterQuizCount(chapter) }} Quiz{{ chapterQuizCount(chapter) === 1 ? ''
                                            : 'zes' }}
                                        </button>

                                        <button v-if="chapter.assignments?.length" type="button"
                                            :disabled="chapter.locked || !isChapterResourcesUnlocked(chapter)"
                                            :data-lock-message="!isChapterResourcesUnlocked(chapter) ? chapterResourceLockMessage(chapter) : null"
                                            :title="!isChapterResourcesUnlocked(chapter) ? chapterResourceLockMessage(chapter) : null"
                                            @click="activePanel = 'assignments'">
                                            <i class="ri-file-list-3-line"></i>
                                            {{ chapter.assignments.length }} Assignment{{ chapter.assignments.length ===
                                            1 ? '' : 's' }}
                                        </button>
                                    </div>
                                </div>
                            </article>
                        </div>

                        <div v-else class="course-learn__empty is-compact">
                            <i class="ri-book-open-line"></i>
                            <strong>No chapters found.</strong>
                        </div>
                    </aside>

                    <section class="course-learn__stage">
                        <nav class="course-learn__tabs" aria-label="Course resources">
                            <button v-for="tab in tabs" :key="tab.value" type="button"
                                :class="{ 'is-active': activePanel === tab.value }" @click="activePanel = tab.value">
                                <i :class="tab.icon"></i>
                                <span>{{ tab.label }}</span>
                                <small>{{ tab.count }}</small>
                            </button>
                        </nav>

                        <div v-if="activePanel === 'lesson'" class="course-learn__lesson-stage">
                            <div class="course-learn__viewer">
                                <template v-if="activeLesson">
                                    <div
                                        ref="mediaShell"
                                        class="course-learn__media-shell"
                                        :class="{
                                            'is-interactive': shouldShowCustomMediaControls(activeLesson),
                                            'is-buffering': isMediaBuffering,
                                            'is-playing': isMediaPlaying,
                                            'is-controls-visible': showCenterControl || !isMediaPlaying || showPlaybackSpeedMenu,
                                        }"
                                        :tabindex="shouldShowCustomMediaControls(activeLesson) ? 0 : undefined"
                                        @mousemove="handleMediaShellPointerMove"
                                        @mouseleave="handleMediaShellPointerLeave"
                                        @keydown="handleMediaShellKeydown"
                                    >
                                        <div class="course-learn__media">
                                            <video
                                                v-if="isVideoLesson(activeLesson) && activeLesson.source !== 'link' && activeLesson.mediaPath"
                                                :key="`video-${activeLesson.id}`" :src="activeLesson.mediaPath"
                                                :poster="activeLesson.thumbnailPath || course.thumbnailPath"
                                                preload="auto"
                                                playsinline
                                                disablepictureinpicture
                                                controlslist="noremoteplayback"
                                                class="course-learn__native-media"
                                                ref="mediaElement"
                                                @loadedmetadata="handleMediaLoaded"
                                                @loadeddata="handleMediaLoaded"
                                                @progress="updateMediaBufferState"
                                                @timeupdate="updatePlaybackState"
                                                @play="startProgressTimer"
                                                @playing="handleMediaPlaying"
                                                @pause="handleMediaPause"
                                                @seeking="handleMediaBuffering"
                                                @waiting="handleMediaBuffering"
                                                @canplay="handleMediaCanPlay"
                                                @canplaythrough="handleMediaCanPlay"
                                                @volumechange="syncMediaVolumeState"
                                                @click="handleMediaClick"
                                                @dblclick="handleMediaDoubleClick"
                                                @ended="handleMediaEnded"></video>

                                            <iframe v-else-if="isVideoLesson(activeLesson) && embeddedLessonUrl"
                                                :key="`embed-${activeLesson.id}`" :src="embeddedLessonUrl"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowfullscreen></iframe>

                                            <div v-else-if="isAudioLesson(activeLesson)" class="course-learn__audio">
                                                <div class="course-learn__audio-icon">
                                                    <i class="ri-volume-up-line"></i>
                                                </div>

                                                <div class="course-learn__audio-copy">
                                                    <span class="course-learn__eyebrow">{{ activeLesson.chapterTitle
                                                        }}</span>
                                                    <h3>{{ activeLesson.title }}</h3>
                                                    <p>Listen to this audio lesson and continue your learning.</p>
                                                </div>

                                                <audio v-if="activeLesson.mediaPath" :key="`audio-${activeLesson.id}`"
                                                    :src="activeLesson.mediaPath"
                                                    preload="auto"
                                                    class="course-learn__native-audio"
                                                    ref="mediaElement"
                                                    @loadedmetadata="handleMediaLoaded"
                                                    @loadeddata="handleMediaLoaded"
                                                    @progress="updateMediaBufferState"
                                                    @timeupdate="updatePlaybackState"
                                                    @play="startProgressTimer"
                                                    @playing="handleMediaPlaying"
                                                    @pause="handleMediaPause"
                                                    @seeking="handleMediaBuffering"
                                                    @waiting="handleMediaBuffering"
                                                    @canplay="handleMediaCanPlay"
                                                    @canplaythrough="handleMediaCanPlay"
                                                    @volumechange="syncMediaVolumeState"
                                                    @ended="handleMediaEnded"></audio>

                                                <!-- <a v-if="activeLesson.mediaPath" :href="activeLesson.mediaPath"
                                                    target="_blank" rel="noopener">
                                                    Open Audio
                                                    <i class="ri-external-link-line"></i>
                                                </a> -->
                                            </div>

                                            <div v-else-if="isDocumentLesson(activeLesson)"
                                                class="course-learn__document">
                                                <div class="course-learn__document-icon">
                                                    <i class="ri-file-text-line"></i>
                                                </div>

                                                <div class="course-learn__document-copy">
                                                    <span class="course-learn__eyebrow">Document lesson</span>
                                                    <h3>{{ activeLesson.title }}</h3>
                                                    <p>
                                                        This lesson includes a document resource. Open it in a new tab
                                                        to read,
                                                        download, or follow along with the material.
                                                    </p>
                                                </div>

                                                <a v-if="activeLesson.mediaPath" :href="activeLesson.mediaPath"
                                                    target="_blank" rel="noopener">
                                                    Open Document
                                                    <i class="ri-external-link-line"></i>
                                                </a>

                                                <span v-else class="course-learn__media-missing">
                                                    No document file attached
                                                </span>
                                            </div>

                                            <div v-else class="course-learn__resource-panel">
                                                <img v-if="activeLesson.thumbnailPath || course.thumbnailPath"
                                                    :src="activeLesson.thumbnailPath || course.thumbnailPath" alt="" />

                                                <i v-else :class="lessonIcon(activeLesson)"></i>

                                                <strong>{{ activeLesson.title }}</strong>

                                                <p>This lesson material is ready to open.</p>

                                                <a v-if="activeLesson.mediaPath" :href="activeLesson.mediaPath"
                                                    target="_blank" rel="noopener">
                                                    Open Material
                                                    <i class="ri-external-link-line"></i>
                                                </a>
                                            </div>

                                            <button
                                                v-if="shouldShowCustomMediaControls(activeLesson)"
                                                type="button"
                                                class="course-learn__media-center-toggle"
                                                :class="{
                                                    'is-paused': !isMediaPlaying,
                                                    'is-visible': showCenterControl || !isMediaPlaying,
                                                }"
                                                :aria-label="isMediaPlaying ? 'Pause media' : 'Play media'"
                                                @click.stop="toggleMediaPlayback"
                                            >
                                                <i :class="isMediaPlaying ? 'ri-pause-fill' : 'ri-play-fill'"></i>
                                            </button>

                                            <div
                                                v-if="skipFeedback"
                                                class="course-learn__media-skip-feedback"
                                                :class="`is-${skipFeedback.direction}`"
                                                aria-hidden="true"
                                            >
                                                <i :class="skipFeedback.direction === 'forward' ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'"></i>
                                                <strong>{{ skipFeedback.label }}</strong>
                                            </div>
                                        </div>

                                        <div v-if="shouldShowCustomMediaControls(activeLesson)" class="course-learn__media-controls">
                                            <button
                                                type="button"
                                                class="course-learn__media-control-button"
                                                :aria-label="isMediaPlaying ? 'Pause media' : 'Play media'"
                                                @click="toggleMediaPlayback"
                                            >
                                                <i :class="isMediaPlaying ? 'ri-pause-fill' : 'ri-play-fill'"></i>
                                            </button>

                                            <span class="course-learn__media-time">
                                                {{ mediaCurrentTimeLabel }} / {{ mediaDurationLabel }}
                                            </span>

                                            <div class="course-learn__media-timeline">
                                                <span
                                                    class="course-learn__media-timeline-track"
                                                    aria-hidden="true"
                                                ></span>
                                                <span
                                                    class="course-learn__media-timeline-buffered"
                                                    :style="{ width: `${bufferedPercent}%` }"
                                                ></span>
                                                <span
                                                    class="course-learn__media-timeline-played"
                                                    :style="{ width: `${playedPercent}%` }"
                                                ></span>

                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="100"
                                                    step="0.1"
                                                    :value="playedPercent"
                                                    class="course-learn__media-range"
                                                    aria-label="Seek media"
                                                    @input="seekMediaFromRange"
                                                />
                                            </div>

                                            <span
                                                class="course-learn__media-buffering"
                                                :class="{ 'is-visible': isMediaBuffering }"
                                                aria-label="Video is loading"
                                            ></span>

                                            <button
                                                type="button"
                                                class="course-learn__media-control-button"
                                                :aria-label="isMediaMuted ? 'Unmute media' : 'Mute media'"
                                                @click="toggleMediaMute"
                                            >
                                                <i :class="isMediaMuted ? 'ri-volume-mute-fill' : 'ri-volume-up-fill'"></i>
                                            </button>

                                            <div v-if="supportsPlaybackSpeedControl" class="course-learn__media-speed">
                                                <button
                                                    type="button"
                                                    class="course-learn__media-control-button is-wide"
                                                    aria-label="Playback speed"
                                                    @click.stop="togglePlaybackSpeedMenu"
                                                >
                                                    {{ playbackRateLabel }}
                                                </button>

                                                <div v-if="showPlaybackSpeedMenu" class="course-learn__media-speed-menu">
                                                    <button
                                                        v-for="option in playbackRateOptions"
                                                        :key="option"
                                                        type="button"
                                                        :class="{ 'is-active': Number(playbackRate.toFixed(2)) === Number(option.toFixed(2)) }"
                                                        @click.stop="setPlaybackRate(option)"
                                                    >
                                                        {{ formatPlaybackRate(option) }}
                                                    </button>
                                                </div>
                                            </div>

                                            <a
                                                v-if="activeLessonDownloadUrl"
                                                :href="activeLessonDownloadUrl"
                                                class="course-learn__media-control-button is-link"
                                                :download="activeLessonDownloadName"
                                                aria-label="Download lesson media"
                                            >
                                                <i class="ri-download-2-line"></i>
                                            </a>

                                            <button
                                                v-if="supportsMediaFullscreen"
                                                type="button"
                                                class="course-learn__media-control-button"
                                                :aria-label="isMediaFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
                                                @click="toggleMediaFullscreen"
                                            >
                                                <i :class="isMediaFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'"></i>
                                            </button>
                                        </div>
                                    </div>

                                    <div class="course-learn__lesson-info">
                                        <div class="course-learn__lesson-heading">
                                            <span class="course-learn__eyebrow">{{ activeLesson.chapterTitle }}</span>
                                            <h2>{{ activeLesson.title }}</h2>
                                        </div>

                                        <span class="course-learn__completion"
                                            :class="{ 'is-done': isLessonCompleted(activeLesson.id) }">
                                            <i
                                                :class="isLessonCompleted(activeLesson.id) ? 'ri-checkbox-circle-fill' : 'ri-time-line'"></i>
                                            {{ lessonCompletionLabel(activeLesson) }}
                                        </span>
                                    </div>

                                    <div v-if="isTimedLesson(activeLesson) && !isLessonCompleted(activeLesson.id)" class="course-learn__notice">
                                        <span class="course-learn__notice-icon">
                                            <i class="ri-information-line"></i>
                                        </span>
                                        <div class="course-learn__notice-copy">
                                            <strong>{{ lessonNoticeTitle(activeLesson) }}</strong>
                                            <p>{{ lessonNoticeText(activeLesson) }}</p>
                                        </div>
                                    </div>

                                    <div v-if="activeLesson.description" class="course-learn__lesson-description-wrap">
                                        <div class="course-learn__lesson-description-head">
                                            <span class="course-learn__eyebrow">Description</span>
                                        </div>
                                        <p class="course-learn__rich-description"
                                            :class="{ 'is-collapsed': shouldShowLessonDescriptionToggle && !lessonDescriptionExpanded }">
                                            {{ activeLesson.description }}
                                        </p>

                                        <button v-if="shouldShowLessonDescriptionToggle" type="button"
                                            class="course-learn__see-more"
                                            @click="lessonDescriptionExpanded = !lessonDescriptionExpanded">
                                            {{ lessonDescriptionExpanded ? 'See Less' : 'See More' }}
                                            <i
                                                :class="lessonDescriptionExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                                        </button>
                                    </div>
                                </template>

                                <div v-else class="course-learn__empty">
                                    <i class="ri-play-circle-line"></i>
                                    <strong>No lesson selected.</strong>
                                </div>
                            </div>
                        </div>

                        <div v-else class="course-learn__resources">
                            <div class="course-learn__resource-head">
                                <div>
                                    <span class="course-learn__eyebrow">{{ activeTab?.label }}</span>
                                    <h2>{{ resourceHeading }}</h2>
                                </div>
                            </div>

                            <div v-if="activeResources.length" class="course-learn__resource-grid">
                                <article v-for="item in activeResources" :key="`${activePanel}-${item.id}`"
                                    class="course-learn__resource-card">
                                    <div class="course-learn__resource-card-head">
                                        <span :class="['course-learn__pill', `is-${item.statusTone || 'active'}`]">
                                            {{ item.statusLabel || 'Available' }}
                                        </span>

                                        <small>{{ item.dateLabel }}</small>
                                    </div>

                                    <h3>{{ item.title }}</h3>
                                    <p>{{ item.chapterTitle || item.courseTitle }}</p>

                                    <dl>
                                        <div v-if="item.durationLabel">
                                            <dt>Duration</dt>
                                            <dd>{{ item.durationLabel }}</dd>
                                        </div>

                                        <div v-if="item.totalQuestions">
                                            <dt>Questions</dt>
                                            <dd>{{ item.totalQuestions }}</dd>
                                        </div>

                                        <div v-if="item.totalMark">
                                            <dt>Total Mark</dt>
                                            <dd>{{ item.totalMark }}</dd>
                                        </div>

                                        <div v-if="item.passMarks || item.passMark">
                                            <dt>Pass Mark</dt>
                                            <dd>{{ item.passMarks || item.passMark }}</dd>
                                        </div>
                                    </dl>

                                    <div class="course-learn__resource-actions">
                                        <a v-if="item.mediaPath" :href="item.mediaPath" target="_blank" rel="noopener">
                                            <i class="ri-attachment-2"></i>
                                            Attachment
                                        </a>

                                        <a v-if="item.actionUrl" :href="item.actionUrl"
                                            class="course-learn__resource-action"
                                            @click="handleResourceAction($event, item)">
                                            {{ actionText(item) }}
                                        </a>

                                        <span v-else class="course-learn__resource-state"
                                            :class="{ 'is-muted': !item.actionEnabled }">
                                            {{ actionText(item) }}
                                        </span>
                                    </div>
                                </article>
                            </div>

                            <div v-else class="course-learn__empty">
                                <i :class="activeTab?.icon || 'ri-folder-open-line'"></i>
                                <strong>No {{ activeTab?.label?.toLowerCase() }} found.</strong>
                            </div>
                        </div>
                    </section>
                </section>
            </main>
        </div>

        <div v-if="pendingAssessment" class="course-learn__confirm" role="dialog" aria-modal="true">
            <div class="course-learn__confirm-card">
                <button type="button" class="course-learn__confirm-close" aria-label="Close"
                    @click="cancelStartAssessment">
                    <i class="ri-close-line"></i>
                </button>

                <span class="course-learn__confirm-icon">
                    <i class="ri-timer-flash-line"></i>
                </span>

                <h2>Start {{ pendingAssessmentType }}?</h2>
                <p>
                    Do you want to start this {{ pendingAssessmentType.toLowerCase() }} now?
                    Once it starts, the timer will run and you cannot skip it.
                </p>

                <div class="course-learn__confirm-actions">
                    <button type="button" class="is-secondary" @click="cancelStartAssessment">Cancel</button>
                    <button type="button" class="is-primary" @click="confirmStartAssessment">
                        Yes, Start {{ pendingAssessmentType }}
                    </button>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import StudentSidebar from '@/components/website/student/StudentSidebar.vue';

const root = document.getElementById('studentCourseLearningComponent')
    || document.getElementById('employeeInternalCourseLearningComponent');

const parseJson = (value, fallback = {}) => {
    try {
        return value ? JSON.parse(value) : fallback;
    } catch (error) {
        return fallback;
    }
};

const student = parseJson(root?.dataset.student);
const embedded = root?.dataset.embedded === 'true';
const backUrl = root?.dataset.backUrl || '/student/enrolled-courses';
const backLabel = root?.dataset.backLabel || 'Enrolled Courses';

const course = reactive(parseJson(root?.dataset.course, {
    chapters: [],
    lessons: [],
    exams: [],
    quizzes: [],
    assignments: [],
    stats: {},
}));

const lessons = computed(() => course.lessons ?? []);
const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
const allowedPanels = ['lesson', 'exams', 'quizzes', 'assignments'];
const requestedPanel = allowedPanels.includes(String(searchParams?.get('panel') || '').toLowerCase())
    ? String(searchParams?.get('panel')).toLowerCase()
    : null;
const requestedLessonId = Number(searchParams?.get('lesson') ?? 0) || null;
const requestedResourceId = Number(searchParams?.get('item') ?? 0) || null;
const requestedChapterId = Number(searchParams?.get('chapter') ?? 0) || null;
const isChapterLockedById = (chapterId) => {
    return Boolean((course.chapters ?? []).find(chapter => Number(chapter.id) === Number(chapterId))?.locked);
};
const isResourceUnlocked = (item) => item?.actionEnabled !== false;
const firstUnlockedLesson = () => lessons.value.find(lesson => isLessonUnlocked(lesson, lesson.chapterId)) ?? lessons.value[0] ?? null;
const requestedLesson = requestedLessonId
    ? lessons.value.find(lesson => Number(lesson.id) === requestedLessonId && isLessonUnlocked(lesson, lesson.chapterId)) ?? null
    : null;
const initialLessonId = requestedLesson?.id ?? firstUnlockedLesson()?.id ?? null;
const initialRequestedChapterId = requestedChapterId && !isChapterLockedById(requestedChapterId) ? requestedChapterId : null;
const initialChapterId = initialRequestedChapterId
    ?? requestedLesson?.chapterId
    ?? course.chapters?.find(chapter => !chapter.locked && (chapter.lessons ?? []).some(lesson => Number(lesson.id) === Number(initialLessonId)))?.id
    ?? course.chapters?.find(chapter => !chapter.locked)?.id
    ?? course.chapters?.[0]?.id
    ?? null;
const initialPanel = requestedPanel ?? 'lesson';

const activePanel = ref(initialPanel);
const activeLessonId = ref(initialLessonId);
const lessonProgressState = ref(initialLessonProgressState(lessons.value));
const completedLessonIds = computed(() => lessons.value
    .filter(lesson => isLessonCompleted(lesson.id))
    .map(lesson => lesson.id));
const courseDescriptionExpanded = ref(false);
const lessonDescriptionExpanded = ref(false);
const openChapterId = ref(initialChapterId);
const pendingAssessment = ref(null);
const mediaElement = ref(null);
const mediaShell = ref(null);
const pendingResumePosition = ref(null);
const bufferedPercent = ref(0);
const playedPercent = ref(0);
const mediaDurationSeconds = ref(0);
const mediaCurrentTimeSeconds = ref(0);
const isMediaPlaying = ref(false);
const isMediaMuted = ref(false);
const isMediaFullscreen = ref(false);
const isMediaBuffering = ref(false);
const playbackRate = ref(1);
const showPlaybackSpeedMenu = ref(false);
const showCenterControl = ref(true);
const skipFeedback = ref(null);
let progressTimer = null;
let mediaOverlayTimer = null;
let skipFeedbackTimer = null;
let isProgressSyncing = false;
let lessonOpenToken = 0;
let lastSyncedSecond = null;
let hasHandledRequestedResource = false;

const activeLesson = computed(() => {
    const selected = lessons.value.find(lesson => Number(lesson.id) === Number(activeLessonId.value) && isLessonUnlocked(lesson, lesson.chapterId));

    return selected ?? firstUnlockedLesson();
});

const shouldShowCourseDescriptionToggle = computed(() => descriptionNeedsToggle(course.description));

const shouldShowLessonDescriptionToggle = computed(() => descriptionNeedsToggle(activeLesson.value?.description));

const summaryStats = computed(() => [
    { label: 'Chapters', value: course.stats?.chapters ?? 0, icon: 'ri-stack-line' },
    { label: 'Lessons', value: course.stats?.lessons ?? 0, icon: 'ri-play-list-2-line' },
    { label: 'Duration', value: minutesLabel(course.stats?.duration ?? 0), icon: 'ri-time-line' },
    { label: 'Exam', value: course.stats?.exams ?? 0, icon: 'ri-file-paper-2-line' },
    { label: 'Quiz', value: course.stats?.quizzes ?? 0, icon: 'ri-question-line' },
    { label: 'Assignment', value: course.stats?.assignments ?? 0, icon: 'ri-file-list-3-line' },
]);

const tabs = computed(() => [
    { label: 'Lesson', value: 'lesson', icon: 'ri-play-circle-line', count: lessons.value.length },
    { label: 'Exam', value: 'exams', icon: 'ri-file-paper-2-line', count: course.exams?.filter(item => item.type === 'exam').length ?? 0 },
    { label: 'Quiz', value: 'quizzes', icon: 'ri-question-line', count: course.quizzes?.length ?? 0 },
    { label: 'Assignment', value: 'assignments', icon: 'ri-file-list-3-line', count: course.assignments?.length ?? 0 },
]);

const activeTab = computed(() => tabs.value.find(tab => tab.value === activePanel.value));

const activeResources = computed(() => {
    return normalizedResourcesForPanel(activePanel.value);
});

const resourceHeading = computed(() => {
    if (activePanel.value === 'assignments') return 'Course Assignments';
    if (activePanel.value === 'quizzes') return 'Course Quizzes';
    return 'Course Exams';
});

const pendingAssessmentType = computed(() => pendingAssessment.value?.typeLabel || 'Assessment');

const embeddedLessonUrl = computed(() => {
    if (!activeLesson.value?.mediaPath) return '';
    return embedUrl(activeLesson.value.mediaPath);
});

const activeLessonProgress = computed(() => {
    if (!activeLesson.value?.id) {
        return null;
    }

    return lessonProgressState.value[activeLesson.value.id] ?? null;
});

const mediaCurrentTimeLabel = computed(() => formatMediaTime(mediaCurrentTimeSeconds.value));
const mediaDurationLabel = computed(() => formatMediaTime(mediaDurationSeconds.value));
const playbackRateOptions = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
const playbackRateLabel = computed(() => formatPlaybackRate(playbackRate.value));
const activeLessonDownloadUrl = computed(() => {
    const lesson = activeLesson.value;

    if (!lesson || lesson.source === 'link') {
        return '';
    }

    return lesson.mediaPath || '';
});
const activeLessonDownloadName = computed(() => downloadFileName(activeLesson.value));
const supportsMediaFullscreen = computed(() => isVideoLesson(activeLesson.value) && isTimedLesson(activeLesson.value));
const supportsPlaybackSpeedControl = computed(() => isTimedLesson(activeLesson.value));

watch(activeLesson, (lesson) => {
    stopProgressTimer();
    lastSyncedSecond = null;
    pendingResumePosition.value = null;
    lessonDescriptionExpanded.value = false;
    resetMediaUiState();
    closePlaybackSpeedMenu();

    if (lesson) {
        openLessonProgress(lesson);
    }
}, { immediate: true });

onMounted(() => {
    document.addEventListener('fullscreenchange', syncFullscreenState);
    document.addEventListener('click', handleDocumentClick);
    maybeOpenRequestedResource();
});

onBeforeUnmount(() => {
    syncActiveMediaProgress(true);
    stopProgressTimer();
    clearMediaOverlayTimer();
    clearSkipFeedbackTimer();
    document.removeEventListener('fullscreenchange', syncFullscreenState);
    document.removeEventListener('click', handleDocumentClick);
});

function isChapterOpen(chapterId) {
    return Number(openChapterId.value) === Number(chapterId);
}

function toggleChapter(chapterId) {
    if (isChapterLockedById(chapterId)) {
        return;
    }

    openChapterId.value = isChapterOpen(chapterId) ? null : chapterId;
}

function chapterExamCount(chapter) {
    return (chapter.exams ?? []).filter(item => item.type === 'exam').length;
}

function chapterQuizCount(chapter) {
    return (chapter.exams ?? []).filter(item => item.type === 'quiz').length;
}

function chapterHasResources(chapter) {
    return chapterExamCount(chapter) || chapterQuizCount(chapter) || (chapter.assignments?.length ?? 0);
}

function isChapterResourcesUnlocked(chapter) {
    return Boolean(chapter?.resourcesUnlocked) && !Boolean(chapter?.locked);
}

function chapterLockMessage() {
    return 'Complete the previous chapter lessons, exams, quizzes, and assignments to unlock this chapter.';
}

function chapterResourceLockMessage(chapter) {
    if (chapter?.locked) {
        return chapterLockMessage(chapter);
    }

    return 'Complete all lessons in this chapter to unlock exams, quizzes, and assignments.';
}

function lessonLockMessage(lesson, chapter) {
    if (chapter?.locked) {
        return chapterLockMessage(chapter);
    }

    return lesson?.lockedReason || 'Complete the previous lesson to unlock this lesson.';
}

function isLessonUnlocked(lesson, chapterId = null) {
    const chapter = (course.chapters ?? []).find(item => Number(item.id) === Number(chapterId ?? lesson?.chapterId));

    if (!chapter || chapter.locked) {
        return false;
    }

    const chapterLessons = chapter.lessons ?? [];
    const lessonIndex = chapterLessons.findIndex(item => Number(item.id) === Number(lesson?.id));

    if (lessonIndex <= 0) {
        return true;
    }

    const previousLesson = chapterLessons[lessonIndex - 1];

    return Boolean(previousLesson) && isLessonCompleted(previousLesson.id);
}

function selectLesson(lesson, chapterId = null) {
    if (!isLessonUnlocked(lesson, chapterId ?? lesson?.chapterId)) {
        return;
    }

    activeLessonId.value = lesson.id;
    activePanel.value = 'lesson';
    lessonDescriptionExpanded.value = false;

    if (chapterId) {
        openChapterId.value = chapterId;
    }
}

function resourcesForPanel(panel) {
    return normalizedResourcesForPanel(panel);
}

function rawResourcesForPanel(panel) {
    if (panel === 'assignments') {
        return course.assignments ?? [];
    }

    if (panel === 'quizzes') {
        return course.quizzes ?? [];
    }

    if (panel === 'exams') {
        return (course.exams ?? []).filter(item => item.type === 'exam');
    }

    return [];
}

function normalizedResourcesForPanel(panel) {
    return rawResourcesForPanel(panel).map(normalizeResourceItem);
}

function chapterById(chapterId) {
    return (course.chapters ?? []).find(item => Number(item.id) === Number(chapterId)) ?? null;
}

function isResourceDueToday(item) {
    const value = item?.examDate || item?.deadline;

    if (!value) {
        return false;
    }

    const today = new Date();
    const todayLabel = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    return String(value) === todayLabel;
}

function buildResourceActionUrl(item) {
    if (item?.availableActionUrl) {
        return item.availableActionUrl;
    }

    const type = String(item?.type || '').toLowerCase();

    if (type === 'assignment') {
        return `/student/assignments/${item.id}/submit`;
    }

    if (type === 'quiz') {
        return `/student/quizzes/${item.id}/take`;
    }

    return `/student/exams/${item.id}/take`;
}

function normalizeResourceItem(item) {
    if (!item) {
        return item;
    }

    const chapter = chapterById(item.chapterId);
    const unlockedNow = !chapter || (!chapter.locked && Boolean(chapter.resourcesUnlocked));

    if (item.status !== 'locked' || !unlockedNow) {
        return item;
    }

    const isAssignment = String(item.type || '').toLowerCase() === 'assignment';
    const dueToday = isResourceDueToday(item);

    return {
        ...item,
        status: dueToday ? 'today' : 'available',
        statusLabel: dueToday ? (isAssignment ? 'Due Today' : 'Today') : 'Available',
        statusTone: dueToday ? 'today' : 'active',
        actionLabel: isAssignment ? 'Submit Assignment' : `Start ${item.typeLabel || 'Exam'}`,
        actionEnabled: true,
        actionUrl: buildResourceActionUrl(item),
    };
}

function maybeOpenRequestedResource() {
    if (hasHandledRequestedResource || !requestedPanel || requestedPanel === 'lesson' || !requestedResourceId) {
        return;
    }

    hasHandledRequestedResource = true;
    const item = resourcesForPanel(requestedPanel)
        .find(resource => Number(resource.id) === Number(requestedResourceId));

    if (!item?.actionEnabled || !item?.actionUrl) {
        return;
    }

    if (needsStartConfirmation(item)) {
        pendingAssessment.value = item;
        return;
    }

    if (requestedPanel === 'assignments') {
        window.location.assign(item.actionUrl);
    }
}

function isLessonCompleted(id) {
    const progress = lessonProgressState.value[id];

    return Boolean(progress?.is_completed)
        && Number(progress?.progress ?? 0) >= 100
        && String(progress?.status || '').toLowerCase() === 'done';
}

function initialLessonProgressState(items) {
    return (items ?? []).reduce((carry, lesson) => {
        carry[lesson.id] = {
            progress: Number(lesson.progress ?? 0),
            current_position: Number(lesson.currentPosition ?? 0),
            total_duration: Number(lesson.totalDuration ?? 0),
            status: lesson.progressStatus || 'not_done',
            is_completed: Boolean(lesson.completed),
            resume_position: Number(lesson.resumePosition ?? lesson.currentPosition ?? 0),
        };

        return carry;
    }, {});
}

function responsePayload(response) {
    return response?.data ?? response ?? {};
}

function updateLessonProgressState(lessonId, payload) {
    const current = lessonProgressState.value[lessonId] ?? {};
    const next = {
        progress: Number(payload.progress ?? current.progress ?? 0),
        current_position: Number(payload.current_position ?? current.current_position ?? 0),
        total_duration: Number(payload.total_duration ?? current.total_duration ?? 0),
        status: payload.status ?? current.status ?? 'not_done',
        is_completed: Boolean(payload.is_completed ?? current.is_completed ?? false),
        resume_position: Number(payload.resume_position ?? payload.current_position ?? current.resume_position ?? 0),
    };

    lessonProgressState.value = {
        ...lessonProgressState.value,
        [lessonId]: next,
    };

    refreshChapterLocks();
}

function isChapterLessonsCompleted(chapter) {
    return (chapter.lessons ?? []).every(lesson => isLessonCompleted(lesson.id));
}

function isChapterCompleted(chapter) {
    const resources = [
        ...(chapter.exams ?? []),
        ...(chapter.assignments ?? []),
    ];

    return isChapterLessonsCompleted(chapter)
        && resources.every(resource => Boolean(resource?.isCompleted));
}

function refreshChapterLocks() {
    let previousChapterCompleted = true;

    (course.chapters ?? []).forEach((chapter) => {
        chapter.locked = !previousChapterCompleted;
        chapter.resourcesUnlocked = previousChapterCompleted && isChapterLessonsCompleted(chapter);
        previousChapterCompleted = isChapterCompleted(chapter);
    });
}

async function openLessonProgress(lesson) {
    if (!lesson?.id || !lesson.progressOpenUrl) {
        return;
    }

    const token = ++lessonOpenToken;

    try {
        const { data } = await window.axios.post(lesson.progressOpenUrl, {
            lesson_id: lesson.id,
            total_duration: estimatedLessonDuration(lesson),
        });
        const payload = responsePayload(data);

        if (token !== lessonOpenToken || Number(activeLesson.value?.id) !== Number(lesson.id)) {
            return;
        }

        updateLessonProgressState(lesson.id, payload);
        pendingResumePosition.value = Number(payload.resume_position ?? 0);
        await nextTick();
        applyResumePosition();
    } catch (error) {
        // Progress tracking should never block lesson viewing.
    }
}

function handleMediaLoaded() {
    mediaDurationSeconds.value = mediaDuration(mediaElement.value, activeLesson.value);
    isMediaBuffering.value = false;
    syncNativeVideoControls();
    syncMediaVolumeState();
    syncPlaybackRate();
    updateMediaBufferState();
    updatePlaybackState();
    showCenterControl.value = true;
    applyResumePosition();
    syncActiveMediaProgress(true);
}

function startProgressTimer() {
    isMediaPlaying.value = true;
    stopProgressTimer();
    showMediaOverlayTemporarily();
    syncActiveMediaProgress(true);
    progressTimer = window.setInterval(() => syncActiveMediaProgress(), 5000);
}

function stopProgressTimer() {
    if (progressTimer) {
        window.clearInterval(progressTimer);
        progressTimer = null;
    }
}

function handleMediaPause() {
    isMediaPlaying.value = false;
    isMediaBuffering.value = false;
    clearMediaOverlayTimer();
    showCenterControl.value = true;
    updatePlaybackState();
    stopProgressTimer();
    syncActiveMediaProgress(true);
}

function handleMediaEnded() {
    isMediaPlaying.value = false;
    isMediaBuffering.value = false;
    clearMediaOverlayTimer();
    showCenterControl.value = true;
    updatePlaybackState();
    stopProgressTimer();
    syncActiveMediaProgress(true);
}

function handleMediaPlaying() {
    isMediaPlaying.value = true;
    isMediaBuffering.value = false;
    updateMediaBufferState();
    updatePlaybackState();
    showMediaOverlayTemporarily();
}

function handleMediaBuffering() {
    isMediaBuffering.value = true;
    updateMediaBufferState();
    updatePlaybackState();
}

function handleMediaCanPlay() {
    isMediaBuffering.value = false;
    updateMediaBufferState();
    updatePlaybackState();
}

function updateMediaBufferState(event) {
    const element = event?.target ?? mediaElement.value;
    const duration = mediaDuration(element, activeLesson.value);

    if (!element) {
        return;
    }

    mediaDurationSeconds.value = duration;
    bufferedPercent.value = duration > 0
        ? clampPercent((resolveBufferedEnd(element, duration) / duration) * 100)
        : 0;
}

function updatePlaybackState(event) {
    const element = event?.target ?? mediaElement.value;
    const duration = mediaDuration(element, activeLesson.value);

    if (!element) {
        return;
    }

    mediaDurationSeconds.value = duration;
    mediaCurrentTimeSeconds.value = Number(element.currentTime || 0);
    playedPercent.value = duration > 0
        ? clampPercent((mediaCurrentTimeSeconds.value / duration) * 100)
        : 0;
}

function shouldShowCustomMediaControls(lesson) {
    return isTimedLesson(lesson);
}

function toggleMediaPlayback() {
    const element = mediaElement.value;

    if (!element) {
        return;
    }

    mediaShell.value?.focus?.({ preventScroll: true });
    showCenterControl.value = true;

    if (element.paused) {
        const playPromise = element.play();

        if (playPromise?.catch) {
            playPromise.catch(() => {
                isMediaPlaying.value = false;
            });
        }

        return;
    }

    element.pause();
}

function handleMediaClick(event) {
    if (event?.detail && event.detail > 1) {
        return;
    }

    toggleMediaPlayback();
}

function handleMediaDoubleClick(event) {
    const element = mediaElement.value;

    if (!element) {
        return;
    }

    const bounds = element.getBoundingClientRect();
    const clickX = Number(event?.clientX ?? bounds.left + bounds.width / 2);
    const relativeX = bounds.width > 0 ? (clickX - bounds.left) / bounds.width : 0.5;

    skipMediaBy(relativeX >= 0.5 ? 10 : -10);
}

function seekMediaFromRange(event) {
    const element = mediaElement.value;
    const duration = mediaDuration(element, activeLesson.value);
    const nextPercent = Number(event?.target?.value ?? 0);

    if (!element || duration <= 0) {
        return;
    }

    mediaShell.value?.focus?.({ preventScroll: true });
    element.currentTime = (clampPercent(nextPercent) / 100) * duration;
    updatePlaybackState();
}

function skipMediaBy(seconds) {
    const element = mediaElement.value;
    const duration = mediaDuration(element, activeLesson.value);

    if (!element || duration <= 0) {
        return;
    }

    element.currentTime = Math.min(Math.max(Number(element.currentTime || 0) + seconds, 0), duration);
    updatePlaybackState();
    showMediaOverlayTemporarily();
    showSkipFeedback(seconds);
}

function handleMediaShellKeydown(event) {
    if (!shouldShowCustomMediaControls(activeLesson.value)) {
        return;
    }

    if (event.key === 'ArrowRight') {
        event.preventDefault();
        skipMediaBy(5);
        return;
    }

    if (event.key === 'ArrowLeft') {
        event.preventDefault();
        skipMediaBy(-5);
        return;
    }

    if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        toggleMediaPlayback();
        return;
    }

    if (event.key.toLowerCase() === 'f' && supportsMediaFullscreen.value) {
        event.preventDefault();
        toggleMediaFullscreen();
    }
}

function handleMediaShellPointerMove() {
    if (!shouldShowCustomMediaControls(activeLesson.value)) {
        return;
    }

    if (!isMediaPlaying.value) {
        showCenterControl.value = true;
        return;
    }

    showMediaOverlayTemporarily();
}

function handleMediaShellPointerLeave() {
    if (!shouldShowCustomMediaControls(activeLesson.value) || !isMediaPlaying.value) {
        return;
    }

    clearMediaOverlayTimer();
    showCenterControl.value = false;
}

function toggleMediaMute() {
    const element = mediaElement.value;

    if (!element) {
        return;
    }

    element.muted = !element.muted;
    syncMediaVolumeState();
}

function togglePlaybackSpeedMenu() {
    showPlaybackSpeedMenu.value = !showPlaybackSpeedMenu.value;
}

function closePlaybackSpeedMenu() {
    showPlaybackSpeedMenu.value = false;
}

function setPlaybackRate(value) {
    const element = mediaElement.value;
    const normalizedValue = Number(value || 1);

    playbackRate.value = normalizedValue;

    if (element) {
        element.playbackRate = normalizedValue;
    }

    closePlaybackSpeedMenu();
}

function syncMediaVolumeState(event) {
    const element = event?.target ?? mediaElement.value;

    if (!element) {
        return;
    }

    isMediaMuted.value = Boolean(element.muted) || Number(element.volume || 0) === 0;
}

function syncPlaybackRate(event) {
    const element = event?.target ?? mediaElement.value;

    if (!element) {
        return;
    }

    element.playbackRate = playbackRate.value;
}

async function toggleMediaFullscreen() {
    const target = mediaShell.value;

    if (document.fullscreenElement) {
        await document.exitFullscreen?.();
        return;
    }

    if (target?.requestFullscreen) {
        await target.requestFullscreen({ navigationUI: 'hide' }).catch(() => target.requestFullscreen());
        syncFullscreenState();
        return;
    }

    if (typeof mediaElement.value?.webkitEnterFullscreen === 'function') {
        mediaElement.value.webkitEnterFullscreen();
    }
}

function syncFullscreenState() {
    isMediaFullscreen.value = document.fullscreenElement === mediaShell.value;
    syncNativeVideoControls();
}

function handleDocumentClick() {
    closePlaybackSpeedMenu();
}

async function syncActiveMediaProgress(force = false) {
    const lesson = activeLesson.value;
    const element = mediaElement.value;

    if (!lesson?.id || !lesson.progressUpdateUrl || !element || !isTimedLesson(lesson) || isProgressSyncing) {
        return;
    }

    const currentTime = Number(element.currentTime || 0);
    const currentSecond = Math.floor(currentTime);

    if (!force && currentSecond === lastSyncedSecond) {
        return;
    }

    const totalDuration = mediaDuration(element, lesson);

    if (!force && totalDuration <= 0) {
        return;
    }

    lastSyncedSecond = currentSecond;
    isProgressSyncing = true;

    try {
        const { data } = await window.axios.post(lesson.progressUpdateUrl, {
            lesson_id: lesson.id,
            current_time: currentTime,
            total_duration: totalDuration,
        });
        updateLessonProgressState(lesson.id, responsePayload(data));
    } catch (error) {
        // Keep playback smooth even if a background progress update fails.
    } finally {
        isProgressSyncing = false;
    }
}

function applyResumePosition() {
    const lesson = activeLesson.value;
    const element = mediaElement.value;

    if (!lesson || !element || !isTimedLesson(lesson)) {
        return;
    }

    const progress = activeLessonProgress.value;
    const resumePosition = Number(pendingResumePosition.value ?? progress?.resume_position ?? 0);

    if (!Number.isFinite(resumePosition) || resumePosition <= 0) {
        return;
    }

    const totalDuration = mediaDuration(element, lesson);
    const boundedPosition = totalDuration > 1
        ? Math.min(resumePosition, totalDuration - 1)
        : resumePosition;

    try {
        if (Math.abs(Number(element.currentTime || 0) - boundedPosition) > 1) {
            element.currentTime = boundedPosition;
        }
    } catch (error) {
        // Some browsers block seeking until enough metadata is loaded.
    }
}

function mediaDuration(element, lesson) {
    const duration = Number(element?.duration || 0);

    if (Number.isFinite(duration) && duration > 0) {
        return duration;
    }

    return estimatedLessonDuration(lesson);
}

function estimatedLessonDuration(lesson) {
    const stateDuration = Number(lessonProgressState.value[lesson?.id]?.total_duration ?? 0);

    if (stateDuration > 0) {
        return stateDuration;
    }

    const lessonDuration = Number(lesson?.totalDuration ?? 0);

    if (lessonDuration > 0) {
        return lessonDuration;
    }

    return Number(lesson?.duration ?? 0) > 0 ? Number(lesson.duration) * 60 : 0;
}

function resolveBufferedEnd(element, duration = 0) {
    const ranges = element?.buffered;

    if (!ranges?.length) {
        return 0;
    }

    let maxBufferedEnd = 0;

    for (let index = 0; index < ranges.length; index += 1) {
        maxBufferedEnd = Math.max(maxBufferedEnd, Number(ranges.end(index) || 0));
    }

    return duration > 0 ? Math.min(maxBufferedEnd, duration) : maxBufferedEnd;
}

function clampPercent(value) {
    return Math.min(Math.max(Number(value || 0), 0), 100);
}

function resetMediaUiState() {
    bufferedPercent.value = 0;
    playedPercent.value = 0;
    mediaDurationSeconds.value = 0;
    mediaCurrentTimeSeconds.value = 0;
    isMediaPlaying.value = false;
    isMediaMuted.value = false;
    isMediaBuffering.value = false;
    showCenterControl.value = true;
    skipFeedback.value = null;
    playbackRate.value = 1;
    clearMediaOverlayTimer();
    clearSkipFeedbackTimer();
}

function enableNativeVideoControls() {
    if (isVideoLesson(activeLesson.value) && mediaElement.value) {
        mediaElement.value.controls = true;
    }
}

function disableNativeVideoControls() {
    if (mediaElement.value) {
        mediaElement.value.controls = false;
    }
}

function syncNativeVideoControls() {
    if (!isVideoLesson(activeLesson.value) || !mediaElement.value) {
        return;
    }

    disableNativeVideoControls();
}

function formatMediaTime(value) {
    const totalSeconds = Math.max(0, Math.floor(Number(value || 0)));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function formatPlaybackRate(value) {
    return `${Number(value || 1)}x`;
}

function clearMediaOverlayTimer() {
    if (mediaOverlayTimer) {
        window.clearTimeout(mediaOverlayTimer);
        mediaOverlayTimer = null;
    }
}

function showMediaOverlayTemporarily() {
    showCenterControl.value = true;

    if (!isMediaPlaying.value) {
        return;
    }

    clearMediaOverlayTimer();
    mediaOverlayTimer = window.setTimeout(() => {
        showCenterControl.value = false;
    }, 1800);
}

function clearSkipFeedbackTimer() {
    if (skipFeedbackTimer) {
        window.clearTimeout(skipFeedbackTimer);
        skipFeedbackTimer = null;
    }
}

function showSkipFeedback(seconds) {
    const absoluteSeconds = Math.abs(Number(seconds || 0));

    skipFeedback.value = {
        direction: seconds >= 0 ? 'forward' : 'backward',
        label: `${seconds >= 0 ? '+' : '-'}${absoluteSeconds}`,
    };

    clearSkipFeedbackTimer();
    skipFeedbackTimer = window.setTimeout(() => {
        skipFeedback.value = null;
    }, 700);
}

function lessonCompletionLabel(lesson) {
    if (isLessonCompleted(lesson?.id)) {
        return 'Completed';
    }

    const progress = Number(lessonProgressState.value[lesson?.id]?.progress ?? 0);

    if (progress > 0) {
        return `${Math.floor(progress)}% ${lessonProgressVerb(lesson)}`;
    }

    return lesson?.durationLabel || 'Flexible';
}

function lessonNoticeTitle(lesson) {
    return isAudioLesson(lesson) ? 'Listen to complete this lesson' : 'Watch to complete this lesson';
}

function lessonNoticeText(lesson) {
    const action = isAudioLesson(lesson) ? 'listen to' : 'watch';

    return `Please ${action} the full lesson to mark it as completed and update your course progress.`;
}

function lessonProgressVerb(lesson) {
    return isAudioLesson(lesson) ? 'listened' : 'watched';
}

function isTimedLesson(lesson) {
    return (isVideoLesson(lesson) && lesson?.source !== 'link' && Boolean(lesson?.mediaPath))
        || (isAudioLesson(lesson) && Boolean(lesson?.mediaPath));
}

function lessonIcon(lesson) {
    const type = String(lesson?.type || '').toLowerCase();

    if (type === 'audio') return 'ri-volume-up-line';
    if (type === 'document') return 'ri-file-text-line';
    if (type === 'note') return 'ri-sticky-note-line';
    if (lesson?.source === 'link') return 'ri-links-line';

    return 'ri-play-circle-line';
}

function lessonTypeLabel(lesson) {
    const type = String(lesson?.type || '').toLowerCase();

    if (lesson?.source === 'link') return 'Link';
    if (type === 'audio') return 'Audio';
    if (type === 'document') return 'Document';
    if (type === 'note') return 'Note';

    return 'Video';
}

function downloadFileName(lesson) {
    const title = String(lesson?.title || 'lesson')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'lesson';
    const extension = fileExtensionFromUrl(lesson?.mediaPath);

    return extension ? `${title}.${extension}` : title;
}

function fileExtensionFromUrl(path) {
    if (typeof path !== 'string' || !path.trim()) {
        return '';
    }

    const cleanPath = path.split('?')[0].split('#')[0];
    const segments = cleanPath.split('.');

    return segments.length > 1 ? String(segments.pop() || '').toLowerCase() : '';
}

function isVideoLesson(lesson) {
    return String(lesson?.type || '').toLowerCase() === 'video';
}

function isAudioLesson(lesson) {
    return String(lesson?.type || '').toLowerCase() === 'audio';
}

function isDocumentLesson(lesson) {
    return String(lesson?.type || '').toLowerCase() === 'document';
}

function isPdf(path) {
    return typeof path === 'string' && path.toLowerCase().split('?')[0].endsWith('.pdf');
}

function embedUrl(url) {
    try {
        const parsed = new URL(url);

        if (parsed.hostname.includes('youtube.com')) {
            const videoId = parsed.searchParams.get('v');
            return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
        }

        if (parsed.hostname.includes('youtu.be')) {
            return `https://www.youtube.com/embed/${parsed.pathname.replace('/', '')}`;
        }

        if (parsed.hostname.includes('vimeo.com')) {
            const videoId = parsed.pathname.split('/').filter(Boolean).pop();
            return videoId ? `https://player.vimeo.com/video/${videoId}` : url;
        }
    } catch (error) {
        return '';
    }

    return '';
}

function minutesLabel(minutes) {
    const value = Number(minutes || 0);

    if (value <= 0) return 'Flexible';
    if (value < 60) return `${value} min`;

    const hours = Math.floor(value / 60);
    const remainingMinutes = value % 60;

    return `${hours} H${remainingMinutes ? ` ${remainingMinutes} min` : ''}`;
}

function descriptionNeedsToggle(value) {
    return String(value || '').trim().length > 180;
}

function actionText(item) {
    if (activePanel.value === 'assignments') {
        return item.actionLabel || 'Submit Assignment';
    }

    return item.actionLabel && item.actionLabel !== 'Start'
        ? item.actionLabel
        : `Start ${item.typeLabel || 'Exam'}`;
}

function needsStartConfirmation(item) {
    const actionLabel = String(item?.actionLabel || '').toLowerCase();
    const status = String(item?.status || '').toLowerCase();

    return ['exam', 'quiz'].includes(String(item?.type || '').toLowerCase())
        && Boolean(item?.actionUrl)
        && !item?.isCompleted
        && actionLabel.startsWith('start')
        && ['available', 'today'].includes(status);
}

function handleResourceAction(event, item) {
    if (!needsStartConfirmation(item)) {
        return;
    }

    event.preventDefault();
    pendingAssessment.value = item;
}

function cancelStartAssessment() {
    pendingAssessment.value = null;
}

function confirmStartAssessment() {
    const url = pendingAssessment.value?.actionUrl;
    pendingAssessment.value = null;

    if (url) {
        window.location.href = url;
    }
}
</script>

<style scoped>
.course-learn {
    background:
        linear-gradient(180deg, #f6f8fb 0%, #eef4f7 45%, #f8fafc 100%);
    padding: 30px 0 48px;
}

.course-learn__container.is-embedded {
    grid-template-columns: minmax(0, 1fr);
    max-width: none;
    padding-inline: 0;
}

.course-learn__container {
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    gap: 24px;
    width: 100%;
    align-items: start;
}

.course-learn__main {
    min-width: 0;
}

.course-learn__hero,
.course-learn__overview,
.course-learn__stat,
.course-learn__curriculum,
.course-learn__stage {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 16px 38px rgba(15, 23, 42, 0.06);
}

.course-learn__hero {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    padding: 20px 22px;
    margin-bottom: 16px;
    border-color: #d7e8f5;
    background:
        linear-gradient(135deg, rgba(13, 110, 253, 0.08), rgba(32, 201, 151, 0.06)),
        #ffffff;
}

.course-learn__hero-copy {
    min-width: 0;
}

.course-learn__eyebrow {
    display: block;
    margin-bottom: 5px;
    color: #0f766e;
    font-size: 11px;
    font-weight: 900;
    letter-spacing: 0;
    text-transform: uppercase;
}

.course-learn__hero h1,
.course-learn__overview h2,
.course-learn__curriculum-head h2,
.course-learn__lesson-info h2,
.course-learn__resource-head h2 {
    margin: 0;
    color: #111827;
    font-weight: 900;
}

.course-learn__hero h1 {
    font-size: 28px;
    line-height: 1.2;
}

.course-learn__back {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 40px;
    padding: 9px 13px;
    border-radius: 8px;
    background: #e7f1ff;
    color: #0d6efd;
    font-size: 13px;
    font-weight: 900;
    text-decoration: none;
    white-space: nowrap;
    transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.course-learn__back:hover {
    background: #dbeafe;
    color: #0b5ed7;
    transform: translateY(-1px);
}

.course-learn__overview {
    padding: 18px 22px;
    margin-bottom: 16px;
    border-color: #dcecff;
}

.course-learn__overview-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 12px;
}

.course-learn__overview h2 {
    font-size: 19px;
}

.course-learn__overview-head button,
.course-learn__see-more {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 34px;
    padding: 7px 11px;
    border: 0;
    border-radius: 8px;
    background: #e7f1ff;
    color: #0d6efd;
    font-size: 12px;
    font-weight: 900;
}

.course-learn__rich-description {
    margin: 0;
    color: #475569;
    font-size: 14px;
    line-height: 1.75;
    text-align: justify;
    white-space: pre-line;
}

.course-learn__rich-description.is-collapsed {
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
}

.course-learn__stats {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 16px;
}

.course-learn__stat {
    display: grid;
    gap: 6px;
    padding: 15px;
}

.course-learn__stat i {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: #eef6ff;
    color: #0d6efd;
    font-size: 19px;
}

.course-learn__stat span {
    color: #64748b;
    font-size: 12px;
    font-weight: 800;
}

.course-learn__stat strong {
    color: #111827;
    font-size: 18px;
    font-weight: 900;
}

.course-learn__workspace {
    display: grid;
    grid-template-columns: minmax(300px, 380px) minmax(0, 1fr);
    gap: 16px;
    align-items: start;
}

.course-learn__curriculum {
    position: sticky;
    top: 18px;
    overflow: hidden;
}

.course-learn__curriculum-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 18px;
    border-bottom: 1px solid #eef2f7;
}

.course-learn__curriculum-head h2,
.course-learn__resource-head h2 {
    font-size: 18px;
}

.course-learn__curriculum-head>span {
    display: inline-flex;
    align-items: center;
    min-height: 30px;
    padding: 5px 10px;
    border-radius: 999px;
    background: #ecfdf5;
    color: #16a34a;
    font-size: 12px;
    font-weight: 900;
}

.course-learn__chapter-list {
    max-height: calc(100vh - 270px);
    overflow: auto;
    padding: 14px;
}

.course-learn__chapter {
    border: 1px solid #eef2f7;
    border-radius: 10px;
    margin-bottom: 12px;
    overflow: hidden;
    background: #ffffff;
    position: relative;
}

.course-learn__chapter:last-child {
    margin-bottom: 0;
}

.course-learn__chapter.is-open {
    border-color: #bfdbfe;
    box-shadow: 0 10px 22px rgba(13, 110, 253, 0.06);
}

.course-learn__chapter-toggle {
    display: grid;
    grid-template-columns: 30px minmax(0, 1fr) auto 20px;
    align-items: center;
    gap: 9px;
    width: 100%;
    padding: 12px;
    border: 0;
    background: #ffffff;
    color: #0f172a;
    text-align: left;
}

.course-learn__chapter-toggle>span {
    display: grid;
    place-items: center;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: #fff7ed;
    color: #f97316;
    font-size: 12px;
    font-weight: 900;
}

.course-learn__chapter.is-completed .course-learn__chapter-toggle>span {
    background: #dcfce7;
    color: #16a34a;
}

.course-learn__chapter-toggle strong {
    overflow: hidden;
    font-size: 13px;
    font-weight: 900;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.course-learn__chapter-toggle small {
    color: #64748b;
    font-size: 11px;
    font-weight: 800;
    white-space: nowrap;
}

.course-learn__chapter-toggle>i {
    color: #94a3b8;
    font-size: 18px;
}

.course-learn__chapter.is-open .course-learn__chapter-toggle {
    background: #f8fbff;
}

.course-learn__chapter.is-locked {
    opacity: 0.72;
    overflow: visible;
}

.course-learn__chapter-toggle:disabled,
.course-learn__lesson:disabled,
.course-learn__chapter-resources button:disabled {
    cursor: not-allowed;
}

.course-learn__chapter.is-locked .course-learn__chapter-toggle {
    background: #f8fafc;
}

.course-learn__chapter.is-locked:hover::after {
    content: attr(data-lock-message);
    position: absolute;
    right: 14px;
    bottom: calc(100% - 6px);
    z-index: 5;
    max-width: 260px;
    padding: 8px 10px;
    border-radius: 8px;
    background: #0f172a;
    color: #ffffff;
    box-shadow: 0 12px 26px rgba(15, 23, 42, 0.18);
    font-size: 11px;
    font-weight: 700;
    line-height: 1.45;
    pointer-events: none;
}

.course-learn__chapter-body {
    display: grid;
    gap: 9px;
    padding: 8px 10px 12px;
}

.course-learn__lesson {
    position: relative;
    display: grid;
    grid-template-columns: 34px minmax(0, 1fr) 20px;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 54px;
    padding: 9px 10px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: #ffffff;
    color: #475569;
    text-align: left;
    transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.course-learn__lesson:hover,
.course-learn__lesson.is-active {
    border-color: #bcd9ff;
    background: #f4f9ff;
    color: #0d6efd;
}

.course-learn__lesson.is-completed {
    border-color: #bbf7d0;
    background: #f0fdf4;
}

.course-learn__lesson.is-locked {
    background: #f8fafc;
    color: #94a3b8;
}

.course-learn__lesson.is-locked:hover::after,
.course-learn__chapter-resources button:disabled:hover::after {
    content: attr(data-lock-message);
    position: absolute;
    left: 12px;
    bottom: calc(100% + 6px);
    z-index: 6;
    width: max-content;
    max-width: 250px;
    padding: 8px 10px;
    border-radius: 8px;
    background: #0f172a;
    color: #ffffff;
    box-shadow: 0 12px 26px rgba(15, 23, 42, 0.18);
    font-size: 11px;
    font-weight: 700;
    line-height: 1.45;
    white-space: normal;
    pointer-events: none;
}

.course-learn__lesson>i:first-child {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: #eef2f7;
    color: currentColor;
    font-size: 17px;
}

.course-learn__lesson>span {
    min-width: 0;
}

.course-learn__lesson strong,
.course-learn__lesson small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.course-learn__lesson strong {
    color: #111827;
    font-size: 13px;
    font-weight: 850;
}

.course-learn__lesson small {
    margin-top: 3px;
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
}

.course-learn__lesson>i:last-child {
    color: #16a34a;
    font-size: 18px;
}

.course-learn__chapter-resources {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    margin-top: 4px;
    padding-left: 4px;
}

.course-learn__chapter-resources button {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    min-height: 28px;
    padding: 5px 8px;
    border: 0;
    border-radius: 8px;
    background: #f8fafc;
    color: #475569;
    font-size: 11px;
    font-weight: 800;
}

.course-learn__stage {
    overflow: hidden;
}

.course-learn__tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 14px;
    border-bottom: 1px solid #eef2f7;
}

.course-learn__tabs button {
    display: inline-grid;
    grid-template-columns: 18px auto 22px;
    align-items: center;
    gap: 7px;
    min-height: 38px;
    padding: 7px 10px;
    border: 1px solid #dbe4ef;
    border-radius: 8px;
    background: #ffffff;
    color: #475569;
    font-size: 12px;
    font-weight: 900;
}

.course-learn__tabs button.is-active {
    border-color: #0d6efd;
    background: #e7f1ff;
    color: #0d6efd;
}

.course-learn__tabs small {
    display: grid;
    place-items: center;
    min-width: 22px;
    height: 22px;
    border-radius: 999px;
    background: rgba(15, 23, 42, 0.08);
    font-size: 11px;
}

.course-learn__lesson-stage,
.course-learn__resources {
    padding: 16px;
}

.course-learn__viewer {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
    background: #ffffff;
}

.course-learn__media-shell {
    position: relative;
    overflow: hidden;
    background: #0f172a;
}

.course-learn__media-shell.is-interactive {
    outline: none;
}

.course-learn__media-shell.is-interactive.is-playing:not(.is-controls-visible) {
    cursor: none;
}

.course-learn__media {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    min-height: 260px;
    background: #0f172a;
}

.course-learn__media-shell:fullscreen {
    width: 100vw;
    height: 100vh;
    border-radius: 0;
    background: #000000;
}

.course-learn__media-shell:fullscreen .course-learn__media {
    width: 100%;
    height: 100%;
    min-height: 100vh;
    aspect-ratio: auto;
    background: #000000;
}

.course-learn__media-shell.is-buffering .course-learn__media::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 4;
    width: 58px;
    height: 58px;
    margin: -29px 0 0 -29px;
    border: 5px solid rgba(255, 255, 255, 0.34);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: course-learn-media-spin 0.82s linear infinite;
    pointer-events: none;
}

@keyframes course-learn-media-spin {
    to {
        transform: rotate(360deg);
    }
}

.course-learn__media video,
.course-learn__media iframe {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    border: 0;
    background: #0f172a;
}

.course-learn__media video {
    object-fit: contain;
}

.course-learn__native-media {
    cursor: inherit;
}

.course-learn__native-audio {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
}

.course-learn__media-center-toggle {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 3;
    display: grid;
    place-items: center;
    width: 68px;
    height: 68px;
    border: 0;
    border-radius: 50%;
    background: rgba(15, 23, 42, 0.46);
    color: #ffffff;
    font-size: 34px;
    transform: translate(-50%, -50%);
    backdrop-filter: blur(8px);
    transition: opacity 0.2s ease, transform 0.2s ease, background-color 0.2s ease;
}

.course-learn__media-center-toggle:not(.is-visible):not(.is-paused) {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.92);
    pointer-events: none;
}

.course-learn__media-center-toggle.is-visible,
.course-learn__media-center-toggle.is-paused {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
}

.course-learn__media-skip-feedback {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 3;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 10px 14px;
    border-radius: 999px;
    background: rgba(15, 23, 42, 0.36);
    color: #ffffff;
    transform: translate(-50%, -50%);
    backdrop-filter: blur(8px);
    pointer-events: none;
}

.course-learn__media-skip-feedback.is-forward {
    transform: translate(calc(-50% + 92px), -50%);
}

.course-learn__media-skip-feedback.is-backward {
    transform: translate(calc(-50% - 92px), -50%);
}

.course-learn__media-skip-feedback i {
    font-size: 22px;
}

.course-learn__media-skip-feedback strong {
    font-size: 28px;
    font-weight: 900;
    letter-spacing: 0.02em;
}

.course-learn__media-controls {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 5;
    display: grid;
    grid-template-columns: auto minmax(116px, auto) 18px auto auto auto auto;
    align-items: center;
    gap: 10px;
    padding: 28px 18px 13px;
    background: linear-gradient(180deg, rgba(15, 23, 42, 0) 0%, rgba(15, 23, 42, 0.82) 45%, rgba(10, 15, 25, 0.95) 100%);
    opacity: 0;
    transform: translateY(8px);
    pointer-events: none;
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.course-learn__media-shell.is-controls-visible .course-learn__media-controls {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
}

.course-learn__media-control-button {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
    color: #ffffff;
    font-size: 18px;
    transition: background-color 0.2s ease;
}

.course-learn__media-control-button.is-link {
    text-decoration: none;
}

.course-learn__media-control-button.is-wide {
    width: auto;
    min-width: 48px;
    padding: 0 12px;
    font-size: 13px;
    font-weight: 800;
}

.course-learn__media-control-button:hover {
    background: rgba(255, 255, 255, 0.14);
}

.course-learn__media-time {
    min-width: 116px;
    color: #e2e8f0;
    font-size: 12px;
    font-weight: 800;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
}

.course-learn__media-timeline {
    position: relative;
    grid-column: 1 / -1;
    order: -1;
    height: 18px;
    margin-bottom: 1px;
}

.course-learn__media-timeline-track,
.course-learn__media-timeline-buffered,
.course-learn__media-timeline-played {
    position: absolute;
    left: 0;
    top: 50%;
    height: 5px;
    border-radius: 999px;
    transform: translateY(-50%);
    pointer-events: none;
}

.course-learn__media-timeline-track {
    width: 100%;
    background: rgba(255, 255, 255, 0.22);
}

.course-learn__media-speed {
    position: relative;
}

.course-learn__media-speed-menu {
    position: absolute;
    right: 0;
    bottom: calc(100% + 10px);
    display: grid;
    gap: 4px;
    min-width: 92px;
    padding: 8px;
    border: 1px solid rgba(148, 163, 184, 0.24);
    border-radius: 10px;
    background: rgba(15, 23, 42, 0.96);
    box-shadow: 0 16px 30px rgba(15, 23, 42, 0.28);
}

.course-learn__media-speed-menu button {
    min-height: 34px;
    padding: 6px 10px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #e2e8f0;
    font-size: 12px;
    font-weight: 800;
    text-align: left;
}

.course-learn__media-speed-menu button:hover,
.course-learn__media-speed-menu button.is-active {
    background: rgba(59, 130, 246, 0.18);
    color: #ffffff;
}

.course-learn__media-timeline-buffered {
    background: rgba(255, 255, 255, 0.68);
}

.course-learn__media-timeline-played {
    background: #ff2d55;
    z-index: 1;
    box-shadow: 0 0 12px rgba(255, 45, 85, 0.2);
}

.course-learn__media-buffering {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: #ffffff;
    border-radius: 50%;
    visibility: hidden;
    animation: course-learn-media-spin 0.82s linear infinite;
}

.course-learn__media-buffering.is-visible {
    visibility: visible;
}

.course-learn__media-range {
    position: absolute;
    inset: 0;
    width: 100%;
    margin: 0;
    appearance: none;
    background: transparent;
    cursor: pointer;
}

.course-learn__media-range::-webkit-slider-runnable-track {
    height: 18px;
    background: transparent;
}

.course-learn__media-range::-moz-range-track {
    height: 18px;
    background: transparent;
    border: 0;
}

.course-learn__media-range::-webkit-slider-thumb {
    appearance: none;
    width: 14px;
    height: 14px;
    margin-top: 2px;
    border: 0;
    border-radius: 50%;
    background: #ff2d55;
    box-shadow: 0 0 0 3px rgba(255, 45, 85, 0.18);
}

.course-learn__media-range::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border: 0;
    border-radius: 50%;
    background: #ff2d55;
    box-shadow: 0 0 0 3px rgba(255, 45, 85, 0.18);
}

.course-learn__audio,
.course-learn__document,
.course-learn__resource-panel {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    align-content: center;
    gap: 16px;
    padding: 34px;
    color: #ffffff;
    text-align: center;
    background:
        radial-gradient(circle at top, rgba(13, 110, 253, 0.22), transparent 34%),
        linear-gradient(135deg, #0f172a 0%, #111827 100%);
}

.course-learn__audio-icon,
.course-learn__document-icon,
.course-learn__resource-panel>i {
    display: grid;
    place-items: center;
    width: 76px;
    height: 76px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
    font-size: 34px;
}

.course-learn__audio-copy,
.course-learn__document>div {
    max-width: 620px;
}

.course-learn__audio h3,
.course-learn__document h3,
.course-learn__resource-panel strong {
    margin: 0;
    color: #ffffff;
    font-size: 21px;
    font-weight: 900;
}

.course-learn__audio p,
.course-learn__document p,
.course-learn__resource-panel p {
    margin: 6px 0 0;
    color: #cbd5e1;
    font-size: 13px;
    line-height: 1.6;
}

.course-learn__audio audio {
    width: min(620px, 100%);
}

.course-learn__audio a,
.course-learn__document a,
.course-learn__resource-panel a,
.course-learn__resource-actions a,
.course-learn__resource-state {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    min-height: 36px;
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 900;
    text-decoration: none;
}

.course-learn__audio a,
.course-learn__document a,
.course-learn__resource-panel a {
    background: #ffffff;
    color: #0f172a;
}

.course-learn__resource-panel img {
    max-width: min(360px, 92%);
    max-height: 210px;
    border-radius: 8px;
    object-fit: cover;
}

.course-learn__lesson-info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px;
    background: #ffffff;
}

.course-learn__lesson-info h2 {
    font-size: 20px;
}

.course-learn__lesson-heading {
    min-width: 0;
}

.course-learn__completion {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 32px;
    padding: 6px 10px;
    border-radius: 999px;
    background: #f8fafc;
    color: #64748b;
    font-size: 12px;
    font-weight: 900;
    white-space: nowrap;
}

.course-learn__completion.is-done {
    background: #ecfdf5;
    color: #16a34a;
}

.course-learn__notice {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
    border-top: 1px solid #eef2f7;
    border-bottom: 1px solid #eef2f7;
    background: linear-gradient(135deg, rgba(13, 110, 253, 0.08), rgba(59, 130, 246, 0.04));
}

.course-learn__notice-icon {
    display: grid;
    place-items: center;
    flex: 0 0 34px;
    width: 34px;
    height: 34px;
    border-radius: 999px;
    background: #dbeafe;
    color: #2563eb;
    font-size: 18px;
}

.course-learn__notice-copy {
    min-width: 0;
}

.course-learn__notice-copy strong {
    display: block;
    color: #1d4ed8;
    font-size: 13px;
    font-weight: 900;
}

.course-learn__notice-copy p {
    margin: 4px 0 0;
    color: #1e3a8a;
    font-size: 13px;
    line-height: 1.65;
}

.course-learn__lesson-description-wrap {
    padding: 16px;
    background: #ffffff;
}

.course-learn__lesson-description-head {
    margin-bottom: 10px;
    padding-top: 2px;
    border-top: 1px solid #eef2f7;
}

.course-learn__lesson-description-head .course-learn__eyebrow {
    margin-bottom: 0;
}

.course-learn__lesson-description-wrap .course-learn__rich-description {
    margin-bottom: 12px;
}

.course-learn__see-more {
    min-height: 32px;
}

.course-learn__resource-head {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
}

.course-learn__resource-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
}

.course-learn__resource-card {
    display: grid;
    gap: 11px;
    padding: 16px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #ffffff;
}

.course-learn__resource-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.course-learn__resource-card-head small {
    color: #64748b;
    font-size: 12px;
    font-weight: 800;
}

.course-learn__pill {
    display: inline-flex;
    align-items: center;
    min-height: 26px;
    padding: 5px 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 900;
}

.course-learn__pill.is-active {
    background: #ecfdf5;
    color: #16a34a;
}

.course-learn__pill.is-completed {
    background: #dcfce7;
    color: #16a34a;
}

.course-learn__pill.is-upcoming {
    background: #f1f5f9;
    color: #64748b;
}

.course-learn__pill.is-today {
    background: #dbeafe;
    color: #1d4ed8;
}

.course-learn__pill.is-expired {
    background: #fef2f2;
    color: #ef4444;
}

.course-learn__pill.is-locked {
    background: #e2e8f0;
    color: #475569;
}

.course-learn__resource-card h3 {
    margin: 0;
    color: #111827;
    font-size: 16px;
    font-weight: 900;
}

.course-learn__resource-card p {
    margin: -4px 0 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 700;
}

.course-learn__resource-card dl {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin: 0;
}

.course-learn__resource-card dl div {
    padding: 9px;
    border-radius: 8px;
    background: #f8fafc;
}

.course-learn__resource-card dt,
.course-learn__resource-card dd {
    margin: 0;
}

.course-learn__resource-card dt {
    color: #64748b;
    font-size: 11px;
    font-weight: 800;
}

.course-learn__resource-card dd {
    margin-top: 3px;
    color: #111827;
    font-size: 13px;
    font-weight: 900;
}

.course-learn__resource-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 2px;
}

.course-learn__resource-actions a {
    border: 1px solid #dbe4ef;
    background: #ffffff;
    color: #0d6efd;
}

.course-learn__resource-actions a.course-learn__resource-action {
    border-color: #0d6efd;
    background: #0d6efd;
    color: #ffffff;
}

.course-learn__resource-state {
    background: #dbeafe;
    color: #1d4ed8;
}

.course-learn__resource-state.is-muted {
    opacity: 0.55;
}

.course-learn__empty {
    display: grid;
    justify-items: center;
    gap: 9px;
    padding: 48px 18px;
    color: #64748b;
    text-align: center;
}

.course-learn__empty.is-compact {
    padding: 32px 16px;
}

.course-learn__empty i {
    display: grid;
    place-items: center;
    width: 54px;
    height: 54px;
    border-radius: 50%;
    background: #e7f1ff;
    color: #0d6efd;
    font-size: 26px;
}

.course-learn__empty strong {
    color: #111827;
    font-size: 15px;
    font-weight: 900;
}

.course-learn__confirm {
    position: fixed;
    inset: 0;
    z-index: 1050;
    display: grid;
    place-items: center;
    padding: 18px;
    background: rgba(15, 23, 42, 0.56);
}

.course-learn__confirm-card {
    position: relative;
    width: min(460px, 100%);
    padding: 28px;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 28px 60px rgba(15, 23, 42, 0.24);
    text-align: center;
}

.course-learn__confirm-close {
    position: absolute;
    top: 12px;
    right: 12px;
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 8px;
    background: #f8fafc;
    color: #64748b;
    font-size: 20px;
}

.course-learn__confirm-icon {
    display: grid;
    place-items: center;
    width: 58px;
    height: 58px;
    margin: 0 auto 14px;
    border-radius: 18px;
    background: #e7f1ff;
    color: #0d6efd;
    font-size: 30px;
}

.course-learn__confirm-card h2 {
    margin: 0;
    color: #111827;
    font-size: 22px;
    font-weight: 900;
}

.course-learn__confirm-card p {
    margin: 10px 0 0;
    color: #475569;
    font-size: 14px;
    line-height: 1.7;
}

.course-learn__confirm-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-top: 22px;
}

.course-learn__confirm-actions button {
    min-height: 42px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 900;
}

.course-learn__confirm-actions .is-secondary {
    border: 1px solid #dbe4ef;
    background: #ffffff;
    color: #334155;
}

.course-learn__confirm-actions .is-primary {
    border: 1px solid #0d6efd;
    background: #0d6efd;
    color: #ffffff;
}

@media (max-width: 1199px) {
    .course-learn__container {
        grid-template-columns: 210px minmax(0, 1fr);
    }

    .course-learn__stats {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .course-learn__workspace {
        grid-template-columns: 320px minmax(0, 1fr);
    }
}

@media (max-width: 991px) {
    .course-learn {
        padding: 24px 0 36px;
    }

    .course-learn__container,
    .course-learn__workspace {
        grid-template-columns: 1fr;
    }

    .course-learn__curriculum {
        position: static;
    }

    .course-learn__chapter-list {
        max-height: none;
    }
}

@media (max-width: 767px) {

    .course-learn__hero,
    .course-learn__overview-head,
    .course-learn__lesson-info {
        flex-direction: column;
        align-items: flex-start;
    }

    .course-learn__back {
        width: 100%;
    }

    .course-learn__stats,
    .course-learn__resource-grid {
        grid-template-columns: 1fr;
    }

    .course-learn__media {
        min-height: 240px;
    }

    .course-learn__audio,
    .course-learn__document,
    .course-learn__resource-panel {
        padding: 24px;
    }

    .course-learn__media-controls {
        grid-template-columns: repeat(5, auto);
        gap: 10px;
    }

    .course-learn__media-time {
        grid-column: 1 / -1;
        order: -2;
        font-size: 11px;
    }

    .course-learn__media-buffering {
        grid-column: auto;
        order: 0;
    }

    .course-learn__media-timeline {
        grid-column: 1 / -1;
        order: -1;
    }

    .course-learn__media-skip-feedback.is-forward {
        transform: translate(calc(-50% + 64px), -50%);
    }

    .course-learn__media-skip-feedback.is-backward {
        transform: translate(calc(-50% - 64px), -50%);
    }

    .course-learn__notice {
        padding: 14px;
    }
}

@media (max-width: 575px) {
    .course-learn__container {
        padding-left: 12px;
        padding-right: 12px;
    }

    .course-learn__hero,
    .course-learn__overview,
    .course-learn__lesson-stage,
    .course-learn__resources {
        padding: 14px;
    }

    .course-learn__tabs button {
        width: 100%;
    }

    .course-learn__resource-card dl {
        grid-template-columns: 1fr;
    }

    .course-learn__chapter-toggle {
        grid-template-columns: 30px minmax(0, 1fr) 20px;
    }

    .course-learn__chapter-toggle small {
        display: none;
    }

    .course-learn__confirm-card {
        padding: 24px 18px;
    }

    .course-learn__confirm-actions {
        grid-template-columns: 1fr;
    }
}
</style>
